/**
 * Linen Lifecycle Dashboard - RFID tracking, inventory, EcoProof, Veteran Linen
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Award, CheckCircle2, Coins, Droplets, Eye, Flag, Leaf,
  Loader2, Package, Plus, Recycle, RefreshCw, Search, Shield, Shirt,
  Sparkles, Tag, Trash2, MapPin, TreePine, Users, Settings,
} from 'lucide-react';
import {
  getLinenInventory, getLinenHistory, registerLinen,
  recordWash, retireLinen, computeEcoScore, getEcoScore,
  flagForInspection, getInspectionQueue, inspectLinen,
  getVeteranStats, configureVeteranRewards, getVeteranRewardConfig,
  guestVeteranOptIn, getGuestEcoRewards,
} from '@/lib/v2Api';

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

const conditionColors = {
  new: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  good: 'bg-teal-500/15 text-teal-500 border-teal-500/30',
  fair: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  worn: 'bg-orange-500/15 text-orange-500 border-orange-500/30',
  stained: 'bg-red-500/15 text-red-400 border-red-500/30',
  damaged: 'bg-red-500/15 text-red-500 border-red-500/30',
};

const LINEN_TYPES = ['bedsheet', 'pillowcase', 'duvet_cover', 'bath_towel', 'hand_towel', 'face_cloth', 'bathrobe', 'pool_towel'];
const FLAG_REASONS = ['stain', 'wear_tear', 'faded', 'discolored'];

// ─── Veteran Stats Hero ───
const VeteranStatsHero = ({ stats }) => {
  if (!stats || stats.veteran_count === 0 && stats.pending_inspections === 0) return null;
  return (
    <Card className="relative overflow-hidden border-emerald-500/20" data-testid="veteran-stats-hero">
      <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_90%_10%,rgba(16,185,129,0.08)_0%,transparent_60%)]" />
      <CardContent className="relative pt-5 pb-4 px-5">
        <div className="flex items-center gap-2 mb-3">
          <Recycle className="h-5 w-5 text-emerald-500" />
          <h3 className="text-base heading-font font-semibold">Veteran Linen Program</h3>
          <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/30 ml-auto">
            {stats.veteran_count} veteran items
          </Badge>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="text-center py-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/50">
            <p className="text-xl heading-font font-bold tabular-nums text-emerald-500">{stats.veteran_count}</p>
            <p className="text-[9px] text-muted-foreground uppercase">Veterans Active</p>
          </div>
          <div className="text-center py-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/50">
            <p className="text-xl heading-font font-bold tabular-nums text-amber-500">{stats.pending_inspections}</p>
            <p className="text-[9px] text-muted-foreground uppercase">Pending Inspect</p>
          </div>
          <div className="text-center py-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/50">
            <p className="text-xl heading-font font-bold tabular-nums">{stats.guest_optins}</p>
            <p className="text-[9px] text-muted-foreground uppercase">Guest Opt-Ins</p>
          </div>
          <div className="text-center py-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/50">
            <p className="text-xl heading-font font-bold tabular-nums text-[hsl(var(--web3-primary))]">{fmt(stats.hermes_distributed)}</p>
            <p className="text-[9px] text-muted-foreground uppercase">HERMES Given</p>
          </div>
          <div className="text-center py-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/50">
            <p className="text-xl heading-font font-bold tabular-nums text-green-500">{stats.kg_saved_from_landfill}</p>
            <p className="text-[9px] text-muted-foreground uppercase">kg Saved</p>
          </div>
        </div>
        {stats.potential_waste_saved_kg > 0 && (
          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <TreePine className="h-3.5 w-3.5 text-emerald-500" />
            {stats.potential_waste_saved_kg} kg of textiles kept from landfill = {stats.co2_equivalent_saved_kg} kg CO2 saved
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Inspection Queue Card ───
const InspectionQueueCard = ({ queue, onInspect }) => {
  if (!queue || queue.length === 0) return null;
  return (
    <Card data-testid="inspection-queue-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Eye className="h-5 w-5 text-amber-500" />
          Inspection Queue ({queue.length})
        </CardTitle>
        <CardDescription>Linens flagged for staff inspection</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[220px]">
          <div className="space-y-1">
            {queue.map((insp) => (
              <div
                key={insp.inspection_id}
                className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => onInspect(insp)}
                data-testid={`inspection-${insp.rfid_tag}`}
              >
                <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                  <Flag className="h-4 w-4 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium font-mono truncate">{insp.rfid_tag}</span>
                    <Badge variant="outline" className="text-[10px] capitalize text-amber-500 border-amber-500/30">
                      {insp.flag_reason.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {insp.linen_type.replace(/_/g, ' ')} | {insp.wash_count} washes | by {insp.flagged_by}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="shrink-0 text-xs">
                  Inspect
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// ─── Inspection Decision Dialog ───
const InspectionDialog = ({ open, onClose, inspection, hotelId, onComplete }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState('');

  if (!inspection) return null;

  const handleDecision = async (decision) => {
    setLoading(decision);
    try {
      await inspectLinen({
        rfid_tag: inspection.rfid_tag,
        hotel_id: hotelId,
        decision,
        inspector_notes: notes,
        inspector_id: 'staff',
      });
      toast.success(decision === 'veteran'
        ? `${inspection.rfid_tag} tagged as Veteran - back in rotation!`
        : `${inspection.rfid_tag} retired from service`
      );
      setNotes('');
      onComplete();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Inspection failed'); }
    finally { setLoading(''); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="inspection-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font">Inspect Linen</DialogTitle>
          <DialogDescription>Review and decide: keep as veteran or retire from service.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">RFID:</span> <span className="font-mono ml-1">{inspection.rfid_tag}</span></div>
            <div><span className="text-muted-foreground">Type:</span> <span className="ml-1 capitalize">{inspection.linen_type?.replace(/_/g, ' ')}</span></div>
            <div><span className="text-muted-foreground">Washes:</span> <span className="ml-1 tabular-nums">{inspection.wash_count}</span></div>
            <div><span className="text-muted-foreground">Reason:</span> <Badge variant="outline" className="ml-1 text-[10px] capitalize text-amber-500 border-amber-500/30">{inspection.flag_reason?.replace(/_/g, ' ')}</Badge></div>
            <div><span className="text-muted-foreground">Condition:</span> <Badge variant="outline" className={`ml-1 text-[10px] capitalize ${conditionColors[inspection.current_condition]}`}>{inspection.current_condition}</Badge></div>
            <div><span className="text-muted-foreground">Flagged by:</span> <span className="ml-1">{inspection.flagged_by}</span></div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Inspector Notes</Label>
            <Textarea
              placeholder="Describe the condition... (e.g., Small faint stain on corner, fabric intact)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="inspector-notes"
            />
          </div>
          <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
            <p className="text-xs text-emerald-400 flex items-center gap-1.5 mb-1">
              <Recycle className="h-3.5 w-3.5" /> Veteran = Still usable
            </p>
            <p className="text-xs text-muted-foreground">
              Tag as veteran if the linen is functional with only cosmetic imperfections.
              It stays in rotation and guests who accept it earn $HERMES rewards.
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="text-red-500 hover:text-red-500 flex-1"
            onClick={() => handleDecision('retire')}
            disabled={!!loading}
            data-testid="decision-retire"
          >
            {loading === 'retire' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Retire
          </Button>
          <Button
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => handleDecision('veteran')}
            disabled={!!loading}
            data-testid="decision-veteran"
          >
            {loading === 'veteran' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Recycle className="h-4 w-4 mr-1" />}
            Tag as Veteran
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Flag Linen Dialog ───
const FlagLinenDialog = ({ open, onClose, item, hotelId, onFlagged }) => {
  const [reason, setReason] = useState('stain');
  const [loading, setLoading] = useState(false);

  if (!item) return null;

  const handleFlag = async () => {
    setLoading(true);
    try {
      await flagForInspection({
        rfid_tag: item.rfid_tag,
        hotel_id: hotelId,
        reason,
        flagged_by: 'housekeeping',
      });
      toast.success(`${item.rfid_tag} flagged for inspection`);
      onFlagged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Flag failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="flag-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font">Flag for Inspection</DialogTitle>
          <DialogDescription>
            Flag <span className="font-mono">{item.rfid_tag}</span> for staff review.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="flag-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FLAG_REASONS.map(r => (
                  <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleFlag} disabled={loading} data-testid="submit-flag-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4 mr-1" />}
            Flag
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Reward Config Dialog ───
const RewardConfigDialog = ({ open, onClose, hotelId, currentConfig, onSaved }) => {
  const [rate, setRate] = useState(currentConfig?.hermes_per_night || 50);
  const [message, setMessage] = useState(currentConfig?.custom_message || '');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await configureVeteranRewards({ hotel_id: hotelId, hermes_per_night: Number(rate), custom_message: message });
      toast.success(`Reward set: ${rate} HERMES/night`);
      onSaved();
    } catch { toast.error('Save failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="reward-config-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font">Configure Eco-Warrior Rewards</DialogTitle>
          <DialogDescription>Set how many $HERMES guests earn per night for choosing veteran linen rooms.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">$HERMES per Night</Label>
            <Input type="number" min={0} value={rate} onChange={(e) => setRate(e.target.value)} data-testid="reward-rate-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Custom Guest Message (optional)</Label>
            <Textarea
              placeholder="e.g., Thank you for choosing sustainability! You'd reuse this at home..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              data-testid="reward-message-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-reward-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Guest Opt-In Dialog ───
const GuestOptInDialog = ({ open, onClose, hotelId, rewardConfig, onOptedIn }) => {
  const [form, setForm] = useState({ guest_id: '', guest_name: '', room_number: '', nights: 1 });
  const [loading, setLoading] = useState(false);

  const handleOptIn = async () => {
    if (!form.guest_id || !form.guest_name || !form.room_number) {
      toast.error('All fields required');
      return;
    }
    setLoading(true);
    try {
      const result = await guestVeteranOptIn({ hotel_id: hotelId, ...form, nights: Number(form.nights) });
      toast.success(`${form.guest_name} earns ${result.total_reward} HERMES! ${result.co2_saved_kg} kg CO2 saved`);
      setForm({ guest_id: '', guest_name: '', room_number: '', nights: 1 });
      onOptedIn();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Opt-in failed'); }
    finally { setLoading(false); }
  };

  const rate = rewardConfig?.hermes_per_night || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="guest-optin-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-500" /> Eco-Warrior Opt-In
          </DialogTitle>
          <DialogDescription>
            Record a guest choosing veteran linen. They earn {rate} HERMES/night.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Guest ID</Label>
            <Input placeholder="GUEST-001" value={form.guest_id} onChange={(e) => setForm(p => ({ ...p, guest_id: e.target.value }))} data-testid="optin-guest-id" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Guest Name</Label>
            <Input placeholder="Jane Doe" value={form.guest_name} onChange={(e) => setForm(p => ({ ...p, guest_name: e.target.value }))} data-testid="optin-guest-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Room</Label>
              <Input placeholder="301" value={form.room_number} onChange={(e) => setForm(p => ({ ...p, room_number: e.target.value }))} data-testid="optin-room" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nights</Label>
              <Input type="number" min={1} value={form.nights} onChange={(e) => setForm(p => ({ ...p, nights: e.target.value }))} data-testid="optin-nights" />
            </div>
          </div>
          <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15 text-center">
            <p className="text-lg heading-font font-bold text-emerald-500">{rate * (Number(form.nights) || 1)} HERMES</p>
            <p className="text-[10px] text-muted-foreground">reward for {form.nights || 1} night(s)</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleOptIn} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-optin-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            Record Opt-In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── EcoProof Score Card ───
const EcoScoreCard = ({ score, onCompute, computing }) => {
  const pct = ((score?.eco_score || 0) / 1000) * 100;
  const color = pct >= 80 ? 'text-emerald-500' : pct >= 60 ? 'text-teal-500' : pct >= 40 ? 'text-amber-500' : 'text-red-500';

  return (
    <Card className="relative overflow-hidden border-emerald-500/15" data-testid="eco-score-card">
      <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_80%_20%,rgba(16,185,129,0.06)_0%,transparent_60%)]" />
      <CardHeader className="relative pb-2">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Leaf className="h-5 w-5 text-emerald-500" />
          EcoProof Score
        </CardTitle>
        <CardDescription>Sustainability score from linen lifecycle data</CardDescription>
      </CardHeader>
      <CardContent className="relative space-y-4">
        <div className="text-center py-2">
          <p className={`text-5xl heading-font font-black tabular-nums ${color}`}>{score?.eco_score || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">/ 1,000</p>
        </div>
        <Progress value={pct} className="h-2.5 [&>div]:bg-emerald-500" />
        {score?.eco_score > 0 && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Avg Utilization</span>
              <span className="tabular-nums font-medium">{score.avg_utilization_pct}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Premature Retirement</span>
              <span className="tabular-nums font-medium">{score.premature_retirement_rate}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Active Health</span>
              <span className="tabular-nums font-medium">{score.active_health_rate}%</span>
            </div>
            <Separator />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Total: {score.total_items} items</span>
              <span>Active: {score.active_items} | Retired: {score.retired_items}</span>
            </div>
          </div>
        )}
        <Button size="sm" variant="outline" className="w-full" onClick={onCompute} disabled={computing} data-testid="compute-eco-btn">
          {computing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Recompute Score
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── Inventory Summary ───
const InventorySummary = ({ summary }) => {
  const types = LINEN_TYPES.filter(t => (summary[t] || 0) > 0);
  return (
    <Card data-testid="inventory-summary-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg heading-font flex items-center gap-2">
          <Package className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
          Inventory ({summary.total} items)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {types.map(t => (
            <div key={t} className="text-center p-2 rounded-lg bg-[hsl(var(--web3-surface-2))]/50">
              <p className="text-xl heading-font font-bold tabular-nums">{summary[t]}</p>
              <p className="text-[9px] text-muted-foreground uppercase">{t.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Item Row ───
const ItemRow = ({ item, onSelect, onFlag }) => {
  const washPct = item.max_wash_cycles > 0 ? (item.wash_count / item.max_wash_cycles) * 100 : 0;
  const cc = conditionColors[item.condition] || conditionColors.good;
  return (
    <div
      className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
      onClick={() => onSelect(item)}
      data-testid={`linen-${item.rfid_tag}`}
    >
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${item.is_veteran ? 'bg-emerald-500/15' : 'bg-[hsl(var(--web3-surface-2))]/80'}`}>
        {item.is_veteran ? <Recycle className="h-4 w-4 text-emerald-500" /> : <Tag className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium font-mono truncate">{item.rfid_tag}</span>
          <Badge variant="outline" className={`text-[10px] capitalize ${cc}`}>{item.condition}</Badge>
          <Badge variant="outline" className="text-[10px] capitalize">{item.status}</Badge>
          {item.is_veteran && (
            <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/30">Veteran</Badge>
          )}
          {item.pending_inspection && (
            <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-500/30">Pending Inspect</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground capitalize">
          {item.linen_type.replace(/_/g, ' ')} | {item.material}
          {item.current_room && <> | Room {item.current_room}</>}
        </p>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {item.status !== 'retired' && !item.pending_inspection && (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-amber-500 hover:text-amber-400"
            onClick={(e) => { e.stopPropagation(); onFlag(item); }}
            title="Flag for inspection"
            data-testid={`flag-${item.rfid_tag}`}
          >
            <Flag className="h-3.5 w-3.5" />
          </Button>
        )}
        <div className="w-20 text-right">
          <p className="text-xs tabular-nums">{item.wash_count}/{item.max_wash_cycles}</p>
          <Progress value={washPct} className="h-1 mt-1" />
        </div>
      </div>
    </div>
  );
};

// ─── Item Detail Dialog ───
const ItemDetailDialog = ({ open, onClose, item, events, hotelId, onAction }) => {
  const [actionLoading, setActionLoading] = useState('');

  if (!item) return null;
  const washPct = item.max_wash_cycles > 0 ? ((item.wash_count / item.max_wash_cycles) * 100).toFixed(1) : 0;

  const doAction = async (action) => {
    setActionLoading(action);
    try {
      if (action === 'wash') {
        await recordWash({ rfid_tag: item.rfid_tag, hotel_id: hotelId, wash_type: 'standard', condition_after: 'good' });
        toast.success('Wash recorded');
      } else if (action === 'retire') {
        await retireLinen({ rfid_tag: item.rfid_tag, hotel_id: hotelId, reason: 'worn_out' });
        toast.success('Item retired');
      }
      onAction();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setActionLoading(''); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="item-detail-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font font-mono text-base flex items-center gap-2">
            {item.rfid_tag}
            {item.is_veteran && <Badge className="bg-emerald-600 text-white text-[10px]">Veteran</Badge>}
          </DialogTitle>
          <DialogDescription className="capitalize">{item.linen_type.replace(/_/g, ' ')} | {item.material}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className="ml-1 text-[10px] capitalize">{item.status}</Badge></div>
            <div><span className="text-muted-foreground">Condition:</span> <Badge variant="outline" className={`ml-1 text-[10px] capitalize ${conditionColors[item.condition]}`}>{item.condition}</Badge></div>
            <div><span className="text-muted-foreground">Room:</span> <span className="ml-1">{item.current_room || '\u2014'}</span></div>
            <div><span className="text-muted-foreground">Wash Count:</span> <span className="ml-1 tabular-nums">{item.wash_count}/{item.max_wash_cycles}</span></div>
          </div>

          {item.is_veteran && (
            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
              <p className="text-xs font-medium text-emerald-400 flex items-center gap-1.5 mb-1">
                <Recycle className="h-3.5 w-3.5" /> Veteran Linen
              </p>
              <p className="text-xs text-muted-foreground">
                Reason: {item.veteran_reason?.replace(/_/g, ' ')} | Since: {new Date(item.veteran_since).toLocaleDateString()}
              </p>
              {item.veteran_notes && <p className="text-xs text-muted-foreground mt-1">Notes: {item.veteran_notes}</p>}
            </div>
          )}

          <div>
            <p className="text-xs text-muted-foreground mb-1">Lifecycle Utilization</p>
            <Progress value={Number(washPct)} className="h-2" />
            <p className="text-[10px] text-muted-foreground text-right mt-0.5">{washPct}%</p>
          </div>

          {item.status !== 'retired' && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => doAction('wash')} disabled={!!actionLoading} data-testid="action-wash">
                {actionLoading === 'wash' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Droplets className="h-3 w-3 mr-1" />} Wash
              </Button>
              <Button size="sm" variant="outline" className="text-red-500 hover:text-red-500" onClick={() => doAction('retire')} disabled={!!actionLoading} data-testid="action-retire">
                {actionLoading === 'retire' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Trash2 className="h-3 w-3 mr-1" />} Retire
              </Button>
            </div>
          )}

          <Separator />
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Event History</p>
          <ScrollArea className="h-[180px]">
            {(events || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No events recorded</p>
            ) : (
              <div className="space-y-2">
                {events.map((ev, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      {ev.event_type === 'wash' && <Droplets className="h-3 w-3 text-blue-500" />}
                      {ev.event_type === 'room_assignment' && <MapPin className="h-3 w-3 text-violet-500" />}
                      {ev.event_type === 'retirement' && <Trash2 className="h-3 w-3 text-red-500" />}
                      {ev.event_type === 'flagged_for_inspection' && <Flag className="h-3 w-3 text-amber-500" />}
                      {ev.event_type === 'inspection_complete' && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                      <span className="capitalize">{ev.event_type.replace(/_/g, ' ')}</span>
                      {ev.room_number && <span className="text-muted-foreground">Room {ev.room_number}</span>}
                      {ev.wash_number && <span className="text-muted-foreground">#{ev.wash_number}</span>}
                      {ev.decision && <Badge variant="outline" className="text-[9px] capitalize">{ev.decision}</Badge>}
                    </div>
                    <span className="text-muted-foreground tabular-nums">{new Date(ev.timestamp).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Register Linen Dialog ───
const RegisterLinenDialog = ({ open, onClose, hotelId, onRegistered }) => {
  const [form, setForm] = useState({ rfid_tag: '', linen_type: 'bedsheet', material: 'cotton' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.rfid_tag) { toast.error('RFID tag required'); return; }
    setSubmitting(true);
    try {
      await registerLinen({ ...form, hotel_id: hotelId });
      toast.success(`${form.rfid_tag} registered`);
      setForm({ rfid_tag: '', linen_type: 'bedsheet', material: 'cotton' });
      onRegistered();
      onClose();
    } catch { toast.error('Registration failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="register-linen-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font">Register Linen Item</DialogTitle>
          <DialogDescription>Scan or enter the RFID tag to add to inventory.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">RFID Tag</Label>
            <Input placeholder="RFID-BED-002" value={form.rfid_tag} onChange={e => setForm(p => ({ ...p, rfid_tag: e.target.value }))} data-testid="input-rfid-tag" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Linen Type</Label>
            <Select value={form.linen_type} onValueChange={v => setForm(p => ({ ...p, linen_type: v }))}>
              <SelectTrigger data-testid="select-linen-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LINEN_TYPES.map(t => (
                  <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Material</Label>
            <Select value={form.material} onValueChange={v => setForm(p => ({ ...p, material: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['cotton', 'egyptian_cotton', 'bamboo', 'microfiber', 'linen', 'polyester'].map(m => (
                  <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-linen-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Register'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ───
export default function LinenDashboardPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [hotelId, setHotelId] = useState('');
  const [inventory, setInventory] = useState(null);
  const [ecoScore, setEcoScore] = useState(null);
  const [veteranStats, setVeteranStats] = useState(null);
  const [inspectionQueue, setInspectionQueue] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showRewardConfig, setShowRewardConfig] = useState(false);
  const [showGuestOptIn, setShowGuestOptIn] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [flagItem, setFlagItem] = useState(null);
  const [showFlag, setShowFlag] = useState(false);
  const [inspectItem, setInspectItem] = useState(null);
  const [showInspect, setShowInspect] = useState(false);

  const loadData = useCallback(async (id, status) => {
    if (!id) return;
    setLoading(true);
    try {
      const [inv, eco, stats, queue] = await Promise.all([
        getLinenInventory(id, status || undefined),
        getEcoScore(id),
        getVeteranStats(id),
        getInspectionQueue(id).then(r => r.queue),
      ]);
      setInventory(inv);
      setEcoScore(eco);
      setVeteranStats(stats);
      setInspectionQueue(queue);
      setHotelId(id);
    } catch { toast.error('Failed to load linen data'); }
    finally { setLoading(false); }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) loadData(searchInput.trim(), statusFilter);
  };

  const handleCompute = async () => {
    setComputing(true);
    try {
      const s = await computeEcoScore(hotelId);
      setEcoScore(s);
      toast.success(`EcoProof score: ${s.eco_score}/1000`);
    } catch { toast.error('Score computation failed'); }
    finally { setComputing(false); }
  };

  const handleSelectItem = async (item) => {
    try {
      const hist = await getLinenHistory(hotelId, item.rfid_tag);
      setSelectedItem(item);
      setSelectedEvents(hist.events || []);
      setShowDetail(true);
    } catch { toast.error('Failed to load item details'); }
  };

  const refresh = () => loadData(hotelId, statusFilter);

  return (
    <div className="admin-theme min-h-dvh bg-background text-foreground">
      <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl heading-font font-semibold tracking-tight" data-testid="page-title">Linen Tracking</h1>
              <p className="text-xs text-muted-foreground">RFID Lifecycle Management & Veteran Linen Program</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hotelId && (
              <>
                <Button size="sm" variant="outline" className="text-emerald-500 hover:text-emerald-400" onClick={() => setShowGuestOptIn(true)} data-testid="guest-optin-btn">
                  <Sparkles className="h-4 w-4 mr-1" /> Eco-Warrior Opt-In
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowRewardConfig(true)} data-testid="reward-config-btn">
                  <Settings className="h-4 w-4 mr-1" /> Rewards
                </Button>
                <Button size="sm" onClick={() => setShowRegister(true)} data-testid="add-linen-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Linen
                </Button>
                <Button variant="outline" size="sm" onClick={refresh} data-testid="refresh-btn">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-2 max-w-lg" data-testid="hotel-search-form">
          <Input
            placeholder="Enter Hotel ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="text-sm flex-1"
            data-testid="hotel-id-input"
          />
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); }}>
            <SelectTrigger className="w-32 h-9 text-xs" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" size="sm" variant="outline" data-testid="search-btn">
            <Search className="h-4 w-4" />
          </Button>
        </form>

        {loading ? (
          <div className="space-y-5">
            <Skeleton className="h-32 w-full" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              <Skeleton className="h-64 lg:col-span-8" />
              <Skeleton className="h-64 lg:col-span-4" />
            </div>
          </div>
        ) : !inventory ? (
          <div className="flex flex-col items-center justify-center py-20 text-center space-y-3">
            <Shirt className="h-12 w-12 text-muted-foreground/40" />
            <p className="text-lg heading-font text-muted-foreground">Enter a Hotel ID to view linen inventory</p>
            <p className="text-sm text-muted-foreground max-w-md">
              Track every bedsheet, towel, and bathrobe through its full lifecycle with RFID.
              Flag worn items for inspection and tag them as veterans to keep them in rotation.
            </p>
          </div>
        ) : (
          <>
            {/* Veteran Stats Hero */}
            <VeteranStatsHero stats={veteranStats} />

            {/* Inspection Queue + Inventory Summary */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <InventorySummary summary={inventory.summary} />
              <InspectionQueueCard queue={inspectionQueue} onInspect={(insp) => { setInspectItem(insp); setShowInspect(true); }} />
            </div>

            {/* Items List + EcoProof Score */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <Card data-testid="linen-list-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg heading-font flex items-center gap-2">
                      <Tag className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
                      Items ({inventory.items.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      {inventory.items.length === 0 ? (
                        <div className="text-center py-12">
                          <Shirt className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                          <p className="text-sm text-muted-foreground">No items match this filter</p>
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          {inventory.items.map(item => (
                            <ItemRow
                              key={item.rfid_tag}
                              item={item}
                              onSelect={handleSelectItem}
                              onFlag={(it) => { setFlagItem(it); setShowFlag(true); }}
                            />
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
              <div className="lg:col-span-4">
                <EcoScoreCard score={ecoScore} onCompute={handleCompute} computing={computing} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Dialogs */}
      <RegisterLinenDialog open={showRegister} onClose={() => setShowRegister(false)} hotelId={hotelId} onRegistered={refresh} />
      <ItemDetailDialog open={showDetail} onClose={() => setShowDetail(false)} item={selectedItem} events={selectedEvents} hotelId={hotelId} onAction={() => { refresh(); setShowDetail(false); }} />
      <FlagLinenDialog open={showFlag} onClose={() => setShowFlag(false)} item={flagItem} hotelId={hotelId} onFlagged={() => { refresh(); setShowFlag(false); }} />
      <InspectionDialog open={showInspect} onClose={() => setShowInspect(false)} inspection={inspectItem} hotelId={hotelId} onComplete={() => { refresh(); setShowInspect(false); }} />
      <RewardConfigDialog open={showRewardConfig} onClose={() => setShowRewardConfig(false)} hotelId={hotelId} currentConfig={veteranStats?.reward_config} onSaved={() => { refresh(); setShowRewardConfig(false); }} />
      <GuestOptInDialog open={showGuestOptIn} onClose={() => setShowGuestOptIn(false)} hotelId={hotelId} rewardConfig={veteranStats?.reward_config} onOptedIn={() => { refresh(); setShowGuestOptIn(false); }} />
    </div>
  );
}
